import {
  Captions,
  ChevronDown,
  ChevronUp,
  Loader2,
  MoreVertical,
  Pause,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Play,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { getRelativeTime } from "@/utils/date";
import axiosInstance from "@/lib/axios";
import { callLogEndpoint } from "@/config/endpoints";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/library/dialog";
import { Button } from "@/components/ui/button";
import SearchableSelector from "@/components/ui/others/SearchableSelector";
import useUsers from "@/features/users/api/useUsers";
import { refetchCallLogs } from "@/features/leads/api/useCallLogs";
import { callDirectionOf } from "@/features/leads/utilities/callDirection";

export interface CallLog {
  id: number;
  phone: string;
  direction: string;
  status: string;
  duration: number;
  recordingUrl: string | null;
  agentNumber: string;
  createdAt: string;
  description: string;
  transcript?: string | null;
  transcriptStatus?: string | null;
  transcriptError?: string | null;
  /** CRM user on the call (device owner / TeleCMI agent). Null when the call
   *  couldn't be attributed to a user. */
  user?: { id: number; name: string } | null;
  category?: string | null;
  /** Cloud-telephony calls: `assignment` says why the lead went (or did not
   *  go) to the person on the call — server callRouting.events.ts. */
  metadata?: {
    assignment?: { result?: string; userId?: number | null; detail?: string } | null;
  } | null;
}

/**
 * Why a provider call (Bonvoice etc.) did NOT give its lead to the person who
 * took it — in words, on the call itself (owner, 2026-10-15: "somebody took
 * the phone, it is supposed to assign them, else tell me the reason"). Null
 * when the hand-over worked or the call is not a provider call. Calls stored
 * before the server noted the reason fall back to the one case the row
 * itself proves: the provider named a number that is on no user.
 */
function assignmentProblem(call: CallLog): string | null {
  if (call.category !== "CLOUD_TELEPHONY") return null;
  const result = call.metadata?.assignment?.result;
  const who = call.user?.name ?? "the person on the call";
  const detail = call.metadata?.assignment?.detail;
  switch (result) {
    case "agent_unknown":
      return `Taken on ${call.agentNumber || "a number"} — no user has this number, so the lead was not assigned. Add it to that person's External call ID (Users → Edit user).`;
    case "no_agent":
      return "The call provider did not say who took this call, so the lead was not assigned.";
    case "other_branch":
      return `Not given to ${who}: the lead is in a branch they are not in.`;
    case "denied":
      return `Not given to ${who}${detail ? `: ${detail}` : ": the assignment rules refused it."}`;
    case "failed":
      return `Could not give the lead to ${who}${detail ? `: ${detail}` : "."}`;
    case "not_answered":
      return `Not answered by ${who}, so the lead was not handed to them. Only a call someone takes hands over the lead.`;
    case "nobody_rung":
      return "The call report named nobody in Xale, so this call changed no assignment. Only a call someone in Xale takes hands over the lead.";
    case "nobody_eligible":
      return "Missed call: no teammate with an External call ID could take the lead.";
    case undefined:
      return !call.user && call.agentNumber
        ? `Taken on ${call.agentNumber} — no user has this number, so the lead was not assigned. Add it to that person's External call ID (Users → Edit user).`
        : null;
    default:
      return null;
  }
}

interface CallHistoryItemProps {
  call: CallLog;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")} min`;
}

function mapStatusToType(status: string): string {
  switch (status.toUpperCase()) {
    case "ANSWERED":
      return "Connected";
    case "MISSED":
      return "Missed";
    case "NO_ANSWER":
    case "NOANSWER":
    case "NO ANSWER":
      return "No Answer";
    default:
      return status;
  }
}

/**
 * On-demand call transcription (server → OpenAI Whisper, cached on the
 * CallLog). States: idle → "Transcribe" action; PROCESSING → spinner +
 * polling; COMPLETED → collapsible transcript; FAILED → error + Retry.
 * The transcript is cached server-side, so re-opening the drawer later
 * renders it instantly from the list payload without another request.
 */
function TranscriptSection({ call }: { call: CallLog }) {
  const [status, setStatus] = useState<string | null>(
    call.transcriptStatus ?? null
  );
  const [transcript, setTranscript] = useState<string | null>(
    call.transcript ?? null
  );
  const [error, setError] = useState<string | null>(
    call.transcriptError ?? null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  const applyResult = (data: {
    transcriptStatus?: string | null;
    transcript?: string | null;
    transcriptError?: string | null;
  }) => {
    setStatus(data.transcriptStatus ?? null);
    setTranscript(data.transcript ?? null);
    setError(data.transcriptError ?? null);
    if (data.transcriptStatus === "COMPLETED") setIsOpen(true);
  };

  // Poll while a transcription job is in flight (a 9-min call takes ~30-60s).
  useEffect(() => {
    if (status !== "PROCESSING") return;
    const timer = setInterval(async () => {
      try {
        const { data } = await axiosInstance.get(
          `${callLogEndpoint}/${call.id}/transcript`
        );
        if (
          data.transcriptStatus === "COMPLETED" ||
          data.transcriptStatus === "FAILED"
        ) {
          applyResult(data);
        }
      } catch {
        // Transient poll failure — keep polling; the worker owns the state.
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [status, call.id]);

  const handleTranscribe = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRequesting(true);
    setError(null);
    try {
      const { data } = await axiosInstance.post(
        `${callLogEndpoint}/${call.id}/transcribe`
      );
      applyResult(data);
    } catch (err: any) {
      setStatus("FAILED");
      setError(
        err?.response?.data?.message || "Could not start transcription"
      );
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <div className="pl-8 w-full" style={{ width: "calc(100% - 2rem)" }}>
      {status === "COMPLETED" && transcript ? (
        <div className="flex flex-col gap-1 w-full">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen((v) => !v);
            }}
            className="flex items-center gap-1.5 text-[12px] font-medium text-[#156548] tracking-[-0.24px] cursor-pointer w-fit"
          >
            <Captions width={14} height={14} />
            Transcript
            {isOpen ? (
              <ChevronUp width={14} height={14} />
            ) : (
              <ChevronDown width={14} height={14} />
            )}
          </button>
          {isOpen && (
            <div className="bg-[#f4f6f5] rounded-xl px-3 py-2 w-full">
              <p className="text-[12px] leading-relaxed text-[#374741] tracking-[-0.24px] whitespace-pre-wrap wrap-break-word">
                {transcript}
              </p>
            </div>
          )}
        </div>
      ) : status === "PROCESSING" ? (
        <div className="flex items-center gap-1.5 text-[12px] text-[#697571] tracking-[-0.24px]">
          <Loader2 width={14} height={14} className="animate-spin" />
          Transcribing…
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleTranscribe}
            disabled={isRequesting}
            className="flex items-center gap-1.5 text-[12px] font-medium text-[#156548] tracking-[-0.24px] cursor-pointer disabled:opacity-50 w-fit"
          >
            {isRequesting ? (
              <Loader2 width={14} height={14} className="animate-spin" />
            ) : (
              <Captions width={14} height={14} />
            )}
            {status === "FAILED" ? "Retry transcription" : "Transcribe"}
          </button>
          {status === "FAILED" && error && (
            <p className="text-[12px] text-red-500 tracking-[-0.24px]">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function CallHistoryItem({ call }: CallHistoryItemProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isWaveformLoading, setIsWaveformLoading] = useState(true);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);

  const type = mapStatusToType(call.status);
  const displayDuration = formatDuration(call.duration);
  // Phone number intentionally omitted from this row — the lead's
  // number is already shown in the header at the top of the panel, so
  // repeating it on every call entry is noise. The direction label
  // ("Outgoing call" / "Incoming call") carries the primary identity.
  // Both vocabularies, any case (INBOUND / INCOMING …) — see callDirection.ts.
  // An exact "INBOUND" test labelled every native INCOMING row "Outgoing".
  const callDir = callDirectionOf(call.direction);
  const directionLabel =
    callDir === "in" ? "Incoming call" : callDir === "out" ? "Outgoing call" : "Call";
  const problem = assignmentProblem(call);

  // "Change caller" — correct a call synced under the wrong person (a shared
  // or reassigned handset uploads under whoever is logged in). The server
  // decides who may (admin, or a manager of the lead's current stage) and
  // audits it on the lead's timeline; the menu offers it to everyone rather
  // than second-guessing that rule client-side.
  const [callerDialogOpen, setCallerDialogOpen] = useState(false);
  const [callerPick, setCallerPick] = useState<string>("");
  const [savingCaller, setSavingCaller] = useState(false);
  const { users } = useUsers(undefined, { isActive: true });
  const callerOptions = (users ?? [])
    .filter((u: any) => u?.id != null && u?.name)
    .map((u: any) => ({ name: String(u.name), value: String(u.id) }));
  const saveCaller = async () => {
    const userId = Number(callerPick);
    if (!Number.isFinite(userId) || userId <= 0) return;
    setSavingCaller(true);
    try {
      const res = await axiosInstance.patch(`${callLogEndpoint}/${call.id}/caller`, { userId });
      toast.success(
        res.data?.unchanged
          ? "That user is already the caller"
          : `Call re-attributed to ${res.data?.userName ?? "the selected user"}`,
      );
      setCallerDialogOpen(false);
      refetchCallLogs();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Couldn't change the caller");
    } finally {
      setSavingCaller(false);
    }
  };

  useEffect(() => {
    if (!call.recordingUrl || !waveformRef.current) {
      setIsWaveformLoading(false);
      return;
    }

    setIsWaveformLoading(true);
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#697571",
      progressColor: "black",
      cursorColor: "#1e302a",
      barWidth: 2,
      barRadius: 1,
      height: 15,
      normalize: true,
      interact: true,
    });
    wavesurfer.load(call.recordingUrl);
    wavesurfer.on("play", () => setIsPlaying(true));
    wavesurfer.on("pause", () => setIsPlaying(false));
    wavesurfer.on("finish", () => setIsPlaying(false));
    wavesurfer.on("ready", () => setIsWaveformLoading(false));
    wavesurferRef.current = wavesurfer;

    return () => {
      wavesurfer.destroy();
      setIsWaveformLoading(true);
    };
  }, [call.recordingUrl]);

  const handleTogglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!wavesurferRef.current) return;
    wavesurferRef.current.playPause();
  };

  const getStatusColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "connected":
        return "text-[color:var(--toggle,#335cff)]";
      case "missed":
        return "text-red-500";
      case "no answer":
        return "text-orange-500";
      default:
        return "text-[#697571]";
    }
  };

  const getCallIcon = (type: string, direction: string) => {
    switch (type.toLowerCase()) {
      case "connected":
        return callDirectionOf(direction) === "in" ? (
          <PhoneIncoming className="text-gray-500" />
        ) : (
          <PhoneOutgoing className="text-gray-500" />
        );
      case "missed":
        return <PhoneMissed className="text-red-500" />;
      case "no answer":
        return <PhoneOff className="text-gray-500" />;
      default:
        return <PhoneIncoming className="" />;
    }
  };

  return (
    <div className="border-b border-[#e6e8e7] flex flex-col gap-2 items-start justify-center px-0 py-3 w-full">
      <div className="flex gap-3 items-center relative w-full">
        {/* Call Icon */}
        <div className="relative shrink-0 size-5 flex items-center justify-center">
          {getCallIcon(type, call.direction)}
        </div>

        {/* Direction & Status — phone number deliberately not repeated;
            it's already in the lead header above. The CRM user on the call IS
            shown (who called / who answered) — the lead header can't tell you
            that. */}
        <div className="flex flex-col gap-0.5 items-start justify-center not-italic relative min-w-0 flex-1">
          <div className="flex items-baseline gap-1 min-w-0 w-full">
            <p className="font-medium leading-normal relative shrink-0 text-[#051912] text-[14px] whitespace-nowrap tracking-[-0.28px]">
              {directionLabel}
            </p>
            {call.user?.name && (
              <p
                className="min-w-0 truncate leading-normal text-[#697571] text-[12px] tracking-[-0.24px]"
                title={
                  callDir === "in"
                    ? `Answered by ${call.user.name}`
                    : `Called by ${call.user.name}`
                }
              >
                · {call.user.name}
              </p>
            )}
          </div>
          <p
            className={`font-normal leading-normal relative shrink-0 text-[12px] whitespace-nowrap tracking-[-0.24px] ${getStatusColor(
              type
            )}`}
          >
            {type} · {displayDuration}
          </p>
          {problem && (
            <p className="leading-normal text-[12px] tracking-[-0.24px] text-[color:var(--color-warning,#ff8447)] whitespace-normal">
              {problem}
            </p>
          )}
        </div>

        {/* Time */}
        <div className="flex items-center relative shrink-0 ml-auto">
          <p
            className={`font-normal leading-normal not-italic relative shrink-0 text-[12px] whitespace-nowrap tracking-[-0.24px] ${type === "Missed" ? "text-red-500" : "text-[#374741]"}`}
          >
            {getRelativeTime(call.createdAt)}
          </p>
        </div>

        {/* More Options */}
        <div className="flex items-center justify-center relative shrink-0 size-8">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Call options"
                className="overflow-clip relative shrink-0 size-5 cursor-pointer"
              >
                <MoreVertical
                  width={20}
                  height={20}
                  className="text-[#1e302a]"
                  strokeWidth={1.5}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => {
                  setCallerPick(call.user?.id != null ? String(call.user.id) : "");
                  setCallerDialogOpen(true);
                }}
              >
                Change caller…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Dialog open={callerDialogOpen} onOpenChange={setCallerDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Who made this call?</DialogTitle>
              <DialogDescription>
                Use this when a call was logged under the wrong person — for
                example a shared handset signed in as someone else. The change
                is recorded on the lead&apos;s activity log.
              </DialogDescription>
            </DialogHeader>
            <SearchableSelector
              options={callerOptions}
              value={callerPick}
              onChange={setCallerPick}
              placeholder="Pick the user"
              searchInputPlaceholder="Search users…"
              triggerClassName="h-10 w-full rounded-xl"
            />
            <DialogFooter>
              <Button variant="cancel" onClick={() => setCallerDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={saveCaller}
                disabled={savingCaller || !callerPick || callerPick === String(call.user?.id ?? "")}
              >
                {savingCaller ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Recording — either the actual waveform player or, when no
          recording is attached to the call log, a disabled-looking
          clone of the player (same chrome, same bar pattern, all
          desaturated + non-interactive) so the row still reserves the
          visual slot and the viewer can tell at a glance "this call has
          no audio". A static fake waveform conveys "audio went here"
          better than a text placeholder. */}
      {!call.recordingUrl && (
        <div
          className="bg-[#f4f6f5] flex items-center pl-8 pr-2 py-1.5 rounded-xl overflow-hidden cursor-not-allowed"
          style={{ width: "calc(100% - 2rem)" }}
          aria-label="No call recording available"
          title="No call recording available"
        >
          <div className="flex grow items-center overflow-clip pr-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full shrink-0 mr-2 opacity-40">
              <Play
                width={16}
                height={16}
                className="translate-x-px text-[#697571]"
              />
            </div>

            <div
              className="flex-1 min-w-0 flex items-center gap-[2px] opacity-50"
              style={{ height: "20px" }}
              aria-hidden="true"
            >
              {/* Deterministic bar pattern — index-driven, not random,
                  so every "no recording" row looks identical and the
                  layout doesn't reflow between renders. */}
              {Array.from({ length: 60 }).map((_, i) => {
                const h =
                  4 +
                  Math.abs(Math.sin(i * 0.45) * 7) +
                  Math.abs(Math.cos(i * 0.7) * 4);
                return (
                  <span
                    key={i}
                    className="w-[2px] shrink-0 rounded-[1px] bg-[#9ba3a0]"
                    style={{ height: `${Math.min(15, h)}px` }}
                  />
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-center pl-2 shrink-0">
            <p className="font-normal italic leading-normal text-[#9ba3a0] text-[12px] whitespace-nowrap tracking-[-0.24px]">
              No recording
            </p>
          </div>
        </div>
      )}
      {call.recordingUrl && (
        <div
          className="bg-[#eaf5f1] flex items-center pl-8 pr-2 py-1.5 rounded-xl overflow-hidden"
          style={{ width: "calc(100% - 2rem)" }}
        >
          <div className="flex grow items-center overflow-clip pr-2">
            <button
              type="button"
              onClick={handleTogglePlay}
              className="flex h-8 w-8 items-center justify-center rounded-full shrink-0 mr-2"
            >
              {isPlaying ? (
                <Pause
                  width={14}
                  height={14}
                  className="cursor-pointer text-black"
                  strokeWidth={2}
                />
              ) : (
                <Play
                  width={16}
                  height={16}
                  className="translate-x-px cursor-pointer text-black"
                />
              )}
            </button>

            <div className="flex-1 min-w-0 relative" style={{ height: "20px" }}>
              {isWaveformLoading && (
                <div className="absolute inset-0 flex items-center overflow-hidden">
                  <div className="wave-loader">
                    {Array.from({ length: 60 }).map((_, i) => (
                      <span
                        key={i}
                        className="wave-bar"
                        style={{ animationDelay: `${i * 0.03}s` }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div
                ref={waveformRef}
                className={isWaveformLoading ? "opacity-0" : "opacity-100"}
                style={{ height: "20px" }}
              />
            </div>
          </div>

          <div className="flex items-center justify-center pl-2 shrink-0">
            <p className="font-normal leading-normal not-italic text-[#374741] text-[12px] whitespace-nowrap tracking-[-0.24px]">
              {displayDuration}
            </p>
          </div>
        </div>
      )}
      {call.recordingUrl && <TranscriptSection call={call} />}
    </div>
  );
}
